import { useCallback, useEffect, useState } from "react";
import { ChefHat, Loader2, Plus, ShieldCheck, TriangleAlert, Utensils } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { DateField, isoDay } from "@/components/common/DateField";
import { CostInsight, SafetyPacks } from "@/components/cafeteria/CostAndPacks";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";

const OK = "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
const BAD = "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300";
const WARN = "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300";

const BLANK_DISH = { name: "", category: "main", price: 0, cost_estimate: 0, diet: "veg",
  allergens: [], notes: "" };
const BLANK_MENU = { on_date: isoDay(new Date()), service: "lunch", dish_refs: [],
  expected_covers: 40, booking_ref: "", notes: "" };
const BLANK_ITEM = { name: "", unit: "kg", level: 0, reorder_point: 0, supplier: "", unit_cost: 0 };
const BLANK_TAKINGS = { on_date: isoDay(new Date()), covers: 0, amount: 0, method: "cash",
  booking_ref: "", note: "" };

const F = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] text-muted-foreground">{hint}</p>}
  </div>
);

export default function Cafeteria() {
  const { can } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [overview, setOverview] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [stock, setStock] = useState(null);
  const [takings, setTakings] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [sheetDay, setSheetDay] = useState(isoDay(new Date()));
  const [readings, setReadings] = useState({});
  const [dish, setDish] = useState(null);
  const [menu, setMenu] = useState(null);
  const [item, setItem] = useState(null);
  const [move, setMove] = useState(null);
  const [money_, setMoney] = useState(null);
  const [busy, setBusy] = useState("");
  const [failure, setFailure] = useState("");
  const canWrite = can("cafeteria", "write");

  const load = useCallback(async () => {
    const [c, o, d, m, s, t] = await Promise.all([
      api.get("/cafeteria/config"), api.get("/cafeteria/overview"),
      api.get("/cafeteria/dishes"), api.get("/cafeteria/menus"),
      api.get("/cafeteria/stock"), api.get("/cafeteria/takings"),
    ]);
    setCfg(c.data); setOverview(o.data); setDishes(d.data.items);
    setMenus(m.data.items); setStock(s.data); setTakings(t.data);
  }, []);

  const loadSheet = useCallback(async (day) => {
    const res = await api.get(`/cafeteria/safety/${day}`);
    setSheet(res.data);
    setReadings({});
  }, []);

  useEffect(() => { load().catch((e) => { setFailure(apiError(e)); toast.error(apiError(e)); }); },
    [load]);
  useEffect(() => { loadSheet(sheetDay).catch((e) => toast.error(apiError(e))); },
    [loadSheet, sheetDay]);

  const act = async (key, fn, message) => {
    setBusy(key);
    let ok = false;
    try {
      const res = await fn();
      toast.success(typeof message === "function" ? message(res) : message);
      await load();
      await loadSheet(sheetDay);
      ok = true;
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
    return ok;
  };

  const fileSheet = async () => {
    const rows = (sheet.checks || []).map((c) => {
      const r = readings[c.code] || {};
      return { code: c.code, value: c.kind === "yes_no" ? null : Number(r.value ?? 0),
        passed: c.kind === "yes_no" ? Boolean(r.passed) : null,
        corrective_action: r.corrective_action || "" };
    });
    await act("sheet", () => api.post("/cafeteria/safety",
      { on_date: sheetDay, readings: rows, signed_by: readings.__signed || "" }),
      (res) => res.data.message);
  };

  if (failure) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Phase 7 · Cafeteria and catering"
                    title="The kitchen is not open to you"
                    description="Your roles do not include access to the cafeteria." />
        <Panel title="Access refused" testId="cafeteria-refused">
          <p className="text-[12.5px] text-muted-foreground" data-testid="cafeteria-refused-message">
            {failure}. Ask a tenant administrator to add the Cafeteria Manager role to your account
            if you need to work in the kitchen.
          </p>
        </Panel>
      </div>
    );
  }

  if (!cfg || !overview || !stock || !takings) return <Loading label="Opening the kitchen" />;

  const outOfRange = (check) => {
    const r = readings[check.code];
    if (!r) return false;
    if (check.kind === "temperature") {
      const v = Number(r.value);
      return r.value !== undefined && r.value !== "" && (v < check.min || v > check.max);
    }
    if (check.kind === "yes_no") return r.answered && !r.passed;
    return false;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Phase 7 · Cafeteria and catering"
        title="Menus, stock and the food-safety record"
        description="Publish the menu with its allergens, keep the store cupboard honest, and file the daily hygiene checks. A reading outside the safe range cannot be filed without saying what you did about it."
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi testId="kpi-menus" label="Menus this month" value={overview.menus.published}
             icon={Utensils} sub={`${overview.menus.served} served`} />
        <Kpi testId="kpi-food-cost" label="Estimated food cost" accent="violet"
             value={money(overview.menus.cost_estimate, "INR", true)} />
        <Kpi testId="kpi-stock-low" label="Low on stock" value={overview.stock.low}
             accent={overview.stock.low ? "amber" : "emerald"} icon={ChefHat}
             sub={`${overview.stock.lines} lines · ${money(overview.stock.value, "INR", true)}`} />
        <Kpi testId="kpi-safety" label="Safety sheets filed"
             value={`${overview.safety.days_filed}/${overview.safety.days_expected}`}
             accent={overview.safety.complete ? "emerald" : "amber"} icon={ShieldCheck}
             sub={overview.safety.breaches ? `${overview.safety.breaches} breaches` : "no breaches"} />
        <Kpi testId="kpi-takings" label="Takings this month" accent="teal"
             value={cfg.sales_mode === "none" ? "catering only"
               : money(overview.takings.total, "INR", true)}
             sub={cfg.sales_mode === "booking" ? "charged to bookings"
               : cfg.sales_mode === "fund" ? "banked to a fund" : "no sales"} />
      </div>

      <Tabs defaultValue="safety">
        <TabsList data-testid="cafeteria-tabs">
          <TabsTrigger value="safety" data-testid="tab-safety">Food safety</TabsTrigger>
          <TabsTrigger value="menus" data-testid="tab-menus">Menus</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock">Stock</TabsTrigger>
          <TabsTrigger value="takings" data-testid="tab-takings">Takings</TabsTrigger>
          <TabsTrigger value="cost" data-testid="tab-cost">Cost &amp; subsidy</TabsTrigger>
          <TabsTrigger value="packs" data-testid="tab-packs">Inspector pack</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-cafeteria-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="safety" className="mt-4 space-y-4">
          <Panel title="Daily checks" testId="panel-safety"
                 kicker={sheet?.sheet ? `Filed by ${sheet.sheet.signed_by}`
                   : "Nothing filed for this day yet"}
                 actions={(
                   <DateField value={sheetDay} clearable={false} testId="safety-date"
                              className="h-9 w-[150px]" onChange={setSheetDay} />
                 )}>
            {sheet?.sheet ? (
              <div className="space-y-2" data-testid="filed-sheet">
                <p className="font-mono text-[11px] text-muted-foreground">
                  {sheet.sheet.sheet_ref} · {shortDate(sheet.sheet.on_date)}
                </p>
                {sheet.sheet.readings.map((r) => (
                  <div key={r.code} data-testid={`filed-${r.code}`}
                       className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-3 py-2">
                    <span className="text-[12.5px] text-foreground">{r.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {r.kind === "yes_no" ? (r.passed ? "yes" : "no")
                          : `${r.value}${r.unit || ""}`}
                      </span>
                      <Chip className={r.in_range ? OK : BAD}>
                        {r.in_range ? "in range" : "corrective action"}
                      </Chip>
                    </div>
                    {!r.in_range && (
                      <p className="w-full text-[11.5px] text-muted-foreground">
                        {r.corrective_action}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2" data-testid="safety-form">
                {(sheet?.checks || []).map((c) => (
                  <div key={c.code} data-testid={`check-${c.code}`}
                       className="grid gap-2 rounded-xl border border-border p-3 lg:grid-cols-[1fr_180px_1fr]">
                    <div>
                      <div className="text-[12.5px] text-foreground">{c.label}</div>
                      <div className="text-[10.5px] text-muted-foreground">
                        {c.frequency}
                        {c.kind === "temperature" ? ` · safe ${c.min} to ${c.max}${c.unit}` : ""}
                      </div>
                    </div>
                    {c.kind === "yes_no" ? (
                      <label className="flex items-center gap-2">
                        <Switch checked={Boolean(readings[c.code]?.passed)} disabled={!canWrite}
                                data-testid={`answer-${c.code}`}
                                onCheckedChange={(v) => setReadings({
                                  ...readings,
                                  [c.code]: { ...readings[c.code], passed: v, answered: true },
                                })} />
                        <span className="text-[11.5px] text-muted-foreground">
                          {readings[c.code]?.passed ? "yes" : "no"}
                        </span>
                      </label>
                    ) : (
                      <Input type="number" step="0.1" className="h-8"
                             placeholder={c.unit} disabled={!canWrite}
                             data-testid={`reading-${c.code}`}
                             value={readings[c.code]?.value ?? ""}
                             onChange={(e) => setReadings({
                               ...readings,
                               [c.code]: { ...readings[c.code], value: e.target.value },
                             })} />
                    )}
                    {outOfRange(c) ? (
                      <Input className="h-8" data-testid={`corrective-${c.code}`}
                             placeholder="What did you do about it?"
                             value={readings[c.code]?.corrective_action || ""}
                             onChange={(e) => setReadings({
                               ...readings,
                               [c.code]: { ...readings[c.code],
                                 corrective_action: e.target.value },
                             })} />
                    ) : <span className="text-[11px] text-muted-foreground">—</span>}
                  </div>
                ))}
                {canWrite && (
                  <div className="flex flex-wrap items-end gap-3 pt-1">
                    <F label="Signed by">
                      <Input className="h-9 w-56" data-testid="safety-signed-by"
                             value={readings.__signed || ""}
                             onChange={(e) => setReadings({ ...readings,
                               __signed: e.target.value })} />
                    </F>
                    <Button onClick={fileSheet} data-testid="safety-file"
                            disabled={busy === "sheet" || (readings.__signed || "").length < 2}
                            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                      {busy === "sheet" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      File the sheet
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Panel>
        </TabsContent>

        <TabsContent value="menus" className="mt-4 space-y-4">
          <Panel title="Dishes" testId="panel-dishes" kicker="Allergens are declared once, here"
                 actions={canWrite && (
                   <Button size="sm" data-testid="add-dish-button" onClick={() => setDish(BLANK_DISH)}
                           className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                     <Plus className="mr-1.5 h-3.5 w-3.5" /> Add a dish
                   </Button>
                 )}>
            <Table testId="dishes-table" head={<><Th>Dish</Th><Th>Diet</Th><Th>Price</Th>
              <Th>Cost</Th><Th>Allergens</Th></>}>
              {dishes.length === 0 && <Empty label="No dishes yet" colSpan={5} />}
              {dishes.map((d) => (
                <tr key={d.dish_ref} className="border-b border-border/60"
                    data-testid={`dish-row-${d.dish_ref}`}>
                  <Td>{d.name}</Td>
                  <Td className="capitalize">{d.diet}</Td>
                  <Td className="font-mono text-[11px]">{money(d.price, "INR")}</Td>
                  <Td className="font-mono text-[11px]">{money(d.cost_estimate, "INR")}</Td>
                  <Td className="text-[11px] text-muted-foreground">
                    {(d.allergens || []).join(", ") || "none declared"}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>

          <Panel title="Menus" testId="panel-menus" kicker="Serving a menu takes its stock out"
                 actions={canWrite && (
                   <Button size="sm" data-testid="publish-menu-button"
                           onClick={() => setMenu(BLANK_MENU)}
                           className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                     <Plus className="mr-1.5 h-3.5 w-3.5" /> Publish a menu
                   </Button>
                 )}>
            <Table testId="menus-table" head={<><Th>Reference</Th><Th>Day</Th><Th>Service</Th>
              <Th>Dishes</Th><Th>Covers</Th><Th>Allergens</Th><Th>Status</Th><Th /></>}>
              {menus.length === 0 && <Empty label="No menus published" colSpan={8} />}
              {menus.map((m) => (
                <tr key={m.menu_ref} className="border-b border-border/60"
                    data-testid={`menu-row-${m.menu_ref}`}>
                  <Td className="font-mono text-[11px]">{m.menu_ref}</Td>
                  <Td>{shortDate(m.on_date)}</Td>
                  <Td className="capitalize">{m.service}</Td>
                  <Td className="text-[11.5px]">
                    {(m.dishes || []).map((d) => d.name).join(", ")}
                  </Td>
                  <Td className="font-mono text-[11px]">{m.expected_covers}</Td>
                  <Td className="text-[11px] text-muted-foreground">
                    {(m.allergen_summary || []).length
                      ? <Chip className={WARN}>{m.allergen_summary.length} declared</Chip>
                      : "none"}
                  </Td>
                  <Td><Chip className={m.status === "served" ? OK : ""}>{m.status}</Chip></Td>
                  <Td>
                    {canWrite && m.status !== "served" && (
                      <Button size="sm" variant="outline" className="rounded-full text-[11px]"
                              data-testid={`serve-${m.menu_ref}`}
                              onClick={() => act(`serve-${m.menu_ref}`,
                                () => api.post(`/cafeteria/menus/${m.menu_ref}/serve`),
                                (res) => res.data.message)}>
                        Serve
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="stock" className="mt-4">
          <Panel title="Store cupboard" testId="panel-stock"
                 kicker={`${stock.counters.low} of ${stock.counters.lines} lines at or below the reorder point`}
                 actions={canWrite && (
                   <Button size="sm" data-testid="add-item-button" onClick={() => setItem(BLANK_ITEM)}
                           className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                     <Plus className="mr-1.5 h-3.5 w-3.5" /> Add an ingredient
                   </Button>
                 )}>
            {stock.counters.low > 0 && (
              <div className="mb-3 flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2"
                   data-testid="low-stock-strip">
                <TriangleAlert className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                <span className="text-[12px] text-amber-700 dark:text-amber-300">
                  {stock.counters.low} ingredient{stock.counters.low === 1 ? "" : "s"} need
                  reordering.
                </span>
              </div>
            )}
            <Table testId="stock-table" head={<><Th>Ingredient</Th><Th>On hand</Th>
              <Th>Reorder at</Th><Th>Supplier</Th><Th>Unit cost</Th><Th /></>}>
              {stock.items.length === 0 && <Empty label="Nothing in stock yet" colSpan={6} />}
              {stock.items.map((i) => (
                <tr key={i.item_ref} className="border-b border-border/60"
                    data-testid={`stock-row-${i.item_ref}`}>
                  <Td>{i.name}</Td>
                  <Td className={`font-mono text-[11px] ${i.low
                    ? "text-amber-600 dark:text-amber-400" : ""}`}>
                    {i.level}{i.unit}
                  </Td>
                  <Td className="font-mono text-[11px]">{i.reorder_point}{i.unit}</Td>
                  <Td className="text-[11.5px] text-muted-foreground">{i.supplier || "—"}</Td>
                  <Td className="font-mono text-[11px]">{money(i.unit_cost, "INR")}</Td>
                  <Td>
                    {canWrite && (
                      <Button size="sm" variant="outline" className="rounded-full text-[11px]"
                              data-testid={`move-${i.item_ref}`}
                              onClick={() => setMove({ item: i, quantity: 1, kind: "received",
                                note: "" })}>
                        Movement
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="takings" className="mt-4">
          <Panel title="Takings" testId="panel-takings"
                 kicker={cfg.sales_mode === "none"
                   ? "This kitchen is set up as catering only — switch sales on in Settings"
                   : cfg.sales_mode === "fund" ? "Banked into the fund set in Settings"
                     : "Charged onto the hirer's booking"}
                 actions={canWrite && cfg.sales_mode !== "none" && (
                   <Button size="sm" data-testid="add-takings-button"
                           onClick={() => setMoney(BLANK_TAKINGS)}
                           className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                     <Plus className="mr-1.5 h-3.5 w-3.5" /> Record takings
                   </Button>
                 )}>
            <Table testId="takings-table" head={<><Th>Reference</Th><Th>Day</Th><Th>Covers</Th>
              <Th>Amount</Th><Th>Method</Th><Th>Where it went</Th></>}>
              {takings.items.length === 0 && <Empty label="No takings recorded" colSpan={6} />}
              {takings.items.map((t) => (
                <tr key={t.takings_ref} className="border-b border-border/60"
                    data-testid={`takings-row-${t.takings_ref}`}>
                  <Td className="font-mono text-[11px]">{t.takings_ref}</Td>
                  <Td>{shortDate(t.on_date)}</Td>
                  <Td className="font-mono text-[11px]">{t.covers}</Td>
                  <Td className="font-mono text-[11px]">{money(t.amount, "INR")}</Td>
                  <Td className="capitalize">{t.method}</Td>
                  <Td className="text-[11.5px] text-muted-foreground">{t.destination}</Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="cost" className="mt-4">
          <CostInsight currency={cfg.currency} />
        </TabsContent>

        <TabsContent value="packs" className="mt-4">
          <SafetyPacks canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <Panel title="How this kitchen runs" testId="panel-cafeteria-settings"
                 kicker="Checklist, sales and where the money goes">
            <div className="grid gap-4 lg:grid-cols-2">
              <F label="Food-safety checklist"
                 hint="Standard uses the ready-made hygiene list; tie it to Compliance and gaps show up against your rules.">
                <Select value={cfg.checklist_mode} disabled={!canWrite}
                        onValueChange={(v) => setCfg({ ...cfg, checklist_mode: v,
                          checks: v === "custom" ? cfg.checks : cfg.standard_checks })}>
                  <SelectTrigger data-testid="checklist-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Ready-made hygiene list</SelectItem>
                    <SelectItem value="custom">We write our own checks</SelectItem>
                    <SelectItem value="standard_with_compliance">
                      Ready-made, tied to the rule engine
                    </SelectItem>
                  </SelectContent>
                </Select>
              </F>
              <F label="Does the kitchen sell?">
                <Select value={cfg.sales_mode} disabled={!canWrite}
                        onValueChange={(v) => setCfg({ ...cfg, sales_mode: v })}>
                  <SelectTrigger data-testid="sales-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No — catering only, track cost and stock</SelectItem>
                    <SelectItem value="fund">Yes — bank the takings into a fund</SelectItem>
                    <SelectItem value="booking">Yes — charge it to a hall booking</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              {cfg.sales_mode === "fund" && (
                <F label="Fund takings are banked into">
                  <Select value={cfg.takings_fund_id || ""} disabled={!canWrite}
                          onValueChange={(v) => setCfg({ ...cfg, takings_fund_id: v })}>
                    <SelectTrigger data-testid="takings-fund"><SelectValue
                      placeholder="Choose a fund" /></SelectTrigger>
                    <SelectContent>
                      {(cfg.funds || []).map((f) => (
                        <SelectItem key={f.code} value={f.code}>{f.code} · {f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
              )}
              {cfg.checklist_mode === "standard_with_compliance" && (
                <F label="Regulatory rule these checks answer to">
                  <Input value={cfg.compliance_rule_code || ""} disabled={!canWrite}
                         data-testid="compliance-rule"
                         onChange={(e) => setCfg({ ...cfg,
                           compliance_rule_code: e.target.value })} />
                </F>
              )}
              <F label="Working hours in a month"
                 hint="Used to turn a salary into an hourly rate for kitchen labour.">
                <Input type="number" data-testid="cost-hours-input" disabled={!canWrite}
                       value={cfg.working_hours_per_month ?? 173}
                       onChange={(e) => setCfg({ ...cfg,
                         working_hours_per_month: Number(e.target.value) })} />
              </F>
              <F label="Overheads per cover"
                 hint="Gas, electricity and water you want carried by each meal.">
                <Input type="number" data-testid="cost-overhead-cover" disabled={!canWrite}
                       value={cfg.overhead_per_cover ?? 0}
                       onChange={(e) => setCfg({ ...cfg,
                         overhead_per_cover: Number(e.target.value) })} />
              </F>
              <F label="Fixed kitchen overheads a month"
                 hint="Apportioned across the days in whatever period you look at.">
                <Input type="number" data-testid="cost-overhead-monthly" disabled={!canWrite}
                       value={cfg.overhead_monthly ?? 0}
                       onChange={(e) => setCfg({ ...cfg,
                         overhead_monthly: Number(e.target.value) })} />
              </F>
              <F label="What a price should recover"
                 hint="The basis the fair price on the Cost & subsidy tab is worked out from.">
                <Select value={cfg.pricing_basis || "fully_loaded"} disabled={!canWrite}
                        onValueChange={(v) => setCfg({ ...cfg, pricing_basis: v })}>
                  <SelectTrigger data-testid="pricing-basis"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(cfg.pricing_bases || {}).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <F label="How much of it to recover (%)"
                 hint="100 breaks even. Put it lower where the charity means to subsidise a meal.">
                <Input type="number" data-testid="pricing-recovery" disabled={!canWrite}
                       value={cfg.pricing_recovery_percent ?? 100}
                       onChange={(e) => setCfg({ ...cfg,
                         pricing_recovery_percent: Number(e.target.value) })} />
              </F>
              <F label="Round prices up to the nearest"
                 hint="So the till is not counting small change.">
                <Input type="number" data-testid="pricing-rounding" disabled={!canWrite}
                       value={cfg.price_rounding ?? 1}
                       onChange={(e) => setCfg({ ...cfg,
                         price_rounding: Number(e.target.value) })} />
              </F>
            </div>
            <div className="mt-4 space-y-2">
              <p className="text-[11px] uppercase tracking-[1.4px] text-muted-foreground">
                Checks on the sheet ({(cfg.checks || []).length})
              </p>
              {(cfg.checks || []).map((c, i) => (
                <div key={c.code} data-testid={`config-check-${c.code}`}
                     className="grid grid-cols-2 gap-2 rounded-xl border border-border p-2 lg:grid-cols-5">
                  <Input value={c.label} className="h-8" disabled={!canWrite}
                         data-testid={`config-label-${c.code}`}
                         onChange={(e) => setCfg({ ...cfg,
                           checks: cfg.checks.map((x, j) => (j === i
                             ? { ...x, label: e.target.value } : x)) })} />
                  <Input value={c.frequency} className="h-8" disabled={!canWrite}
                         onChange={(e) => setCfg({ ...cfg,
                           checks: cfg.checks.map((x, j) => (j === i
                             ? { ...x, frequency: e.target.value } : x)) })} />
                  <Input value={c.kind} className="h-8" disabled />
                  <Input type="number" value={c.min ?? ""} className="h-8" disabled={!canWrite}
                         placeholder="min" data-testid={`config-min-${c.code}`}
                         onChange={(e) => setCfg({ ...cfg,
                           checks: cfg.checks.map((x, j) => (j === i
                             ? { ...x, min: e.target.value === "" ? null
                               : Number(e.target.value) } : x)) })} />
                  <Input type="number" value={c.max ?? ""} className="h-8" disabled={!canWrite}
                         placeholder="max" data-testid={`config-max-${c.code}`}
                         onChange={(e) => setCfg({ ...cfg,
                           checks: cfg.checks.map((x, j) => (j === i
                             ? { ...x, max: e.target.value === "" ? null
                               : Number(e.target.value) } : x)) })} />
                </div>
              ))}
              {canWrite && (
                <Button onClick={() => act("config", () => api.put("/cafeteria/config", {
                          checklist_mode: cfg.checklist_mode, checks: cfg.checks,
                          sales_mode: cfg.sales_mode, takings_fund_id: cfg.takings_fund_id || "",
                          compliance_rule_code: cfg.compliance_rule_code || "",
                          working_hours_per_month: Number(cfg.working_hours_per_month) || 173,
                          overhead_per_cover: Number(cfg.overhead_per_cover) || 0,
                          overhead_monthly: Number(cfg.overhead_monthly) || 0,
                          pricing_basis: cfg.pricing_basis || "fully_loaded",
                          pricing_recovery_percent:
                            Number(cfg.pricing_recovery_percent ?? 100),
                          price_rounding: Number(cfg.price_rounding) || 1,
                        }), "Cafeteria settings saved")}
                        disabled={busy === "config"} data-testid="cafeteria-config-save"
                        className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                  {busy === "config" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
                </Button>
              )}
            </div>
          </Panel>
        </TabsContent>
      </Tabs>

      {/* dish */}
      <Dialog open={Boolean(dish)} onOpenChange={(o) => !o && setDish(null)}>
        <DialogContent data-testid="dish-dialog">
          <DialogHeader><DialogTitle>Add a dish</DialogTitle>
            <DialogDescription>
              Declare the allergens once here and every menu carries them.
            </DialogDescription></DialogHeader>
          {dish && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <F label="Name"><Input value={dish.name} data-testid="dish-name"
                  onChange={(e) => setDish({ ...dish, name: e.target.value })} /></F>
                <F label="Diet">
                  <Select value={dish.diet} onValueChange={(v) => setDish({ ...dish, diet: v })}>
                    <SelectTrigger data-testid="dish-diet"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(cfg.diets || []).map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Price"><Input type="number" value={dish.price} data-testid="dish-price"
                  onChange={(e) => setDish({ ...dish, price: Number(e.target.value) })} /></F>
                <F label="Cost estimate per cover">
                  <Input type="number" value={dish.cost_estimate} data-testid="dish-cost"
                         onChange={(e) => setDish({ ...dish,
                           cost_estimate: Number(e.target.value) })} /></F>
              </div>
              <F label="Allergens">
                <div className="max-h-40 space-y-1 overflow-auto rounded-xl border border-border p-2"
                     data-testid="dish-allergens">
                  {(cfg.allergens || []).map((a) => (
                    <label key={a} className="flex items-center gap-2">
                      <Checkbox checked={dish.allergens.includes(a)}
                                data-testid={`allergen-${a.split(" ")[0]}`}
                                onCheckedChange={() => setDish({
                                  ...dish,
                                  allergens: dish.allergens.includes(a)
                                    ? dish.allergens.filter((x) => x !== a)
                                    : [...dish.allergens, a],
                                })} />
                      <span className="text-[12px] text-muted-foreground">{a}</span>
                    </label>
                  ))}
                </div>
              </F>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="dish-save" disabled={busy === "dish"
                      || (dish?.name || "").trim().length < 2}
                    onClick={() => act("dish", () => api.post("/cafeteria/dishes", dish),
                      "Dish added").then((ok) => ok && setDish(null))}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              Add the dish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* menu */}
      <Dialog open={Boolean(menu)} onOpenChange={(o) => !o && setMenu(null)}>
        <DialogContent data-testid="menu-dialog">
          <DialogHeader><DialogTitle>Publish a menu</DialogTitle>
            <DialogDescription>Pick the dishes and how many you expect to serve.
            </DialogDescription></DialogHeader>
          {menu && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <F label="Day">
                  <DateField value={menu.on_date} clearable={false} testId="menu-date"
                             onChange={(v) => setMenu({ ...menu, on_date: v })} /></F>
                <F label="Service">
                  <Select value={menu.service}
                          onValueChange={(v) => setMenu({ ...menu, service: v })}>
                    <SelectTrigger data-testid="menu-service"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["breakfast", "lunch", "tea", "dinner"].map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select></F>
                <F label="Expected covers">
                  <Input type="number" value={menu.expected_covers} data-testid="menu-covers"
                         onChange={(e) => setMenu({ ...menu,
                           expected_covers: Number(e.target.value) })} /></F>
              </div>
              <F label="Dishes">
                <div className="max-h-44 space-y-1 overflow-auto rounded-xl border border-border p-2"
                     data-testid="menu-dishes">
                  {dishes.map((d) => (
                    <label key={d.dish_ref} className="flex items-center gap-2">
                      <Checkbox checked={menu.dish_refs.includes(d.dish_ref)}
                                data-testid={`menu-dish-${d.dish_ref}`}
                                onCheckedChange={() => setMenu({
                                  ...menu,
                                  dish_refs: menu.dish_refs.includes(d.dish_ref)
                                    ? menu.dish_refs.filter((x) => x !== d.dish_ref)
                                    : [...menu.dish_refs, d.dish_ref],
                                })} />
                      <span className="text-[12px] text-foreground">{d.name}</span>
                      <span className="text-[10.5px] text-muted-foreground">
                        {(d.allergens || []).join(", ")}
                      </span>
                    </label>
                  ))}
                </div>
              </F>
              <F label="Against a hall booking (optional)">
                <Input value={menu.booking_ref} data-testid="menu-booking"
                       onChange={(e) => setMenu({ ...menu, booking_ref: e.target.value })} /></F>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="menu-save" disabled={busy === "menu"
                      || !(menu?.dish_refs || []).length}
                    onClick={() => act("menu", () => api.post("/cafeteria/menus", menu),
                      (res) => `${res.data.menu_ref} published`).then((ok) => ok && setMenu(null))}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              Publish it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* stock item */}
      <Dialog open={Boolean(item)} onOpenChange={(o) => !o && setItem(null)}>
        <DialogContent data-testid="item-dialog">
          <DialogHeader><DialogTitle>Add an ingredient</DialogTitle></DialogHeader>
          {item && (
            <div className="grid grid-cols-2 gap-3">
              <F label="Name"><Input value={item.name} data-testid="item-name"
                onChange={(e) => setItem({ ...item, name: e.target.value })} /></F>
              <F label="Unit"><Input value={item.unit} data-testid="item-unit"
                onChange={(e) => setItem({ ...item, unit: e.target.value })} /></F>
              <F label="On hand"><Input type="number" value={item.level} data-testid="item-level"
                onChange={(e) => setItem({ ...item, level: Number(e.target.value) })} /></F>
              <F label="Reorder at"><Input type="number" value={item.reorder_point}
                data-testid="item-reorder"
                onChange={(e) => setItem({ ...item,
                  reorder_point: Number(e.target.value) })} /></F>
              <F label="Supplier"><Input value={item.supplier} data-testid="item-supplier"
                onChange={(e) => setItem({ ...item, supplier: e.target.value })} /></F>
              <F label="Unit cost"><Input type="number" value={item.unit_cost}
                data-testid="item-cost"
                onChange={(e) => setItem({ ...item, unit_cost: Number(e.target.value) })} /></F>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="item-save" disabled={busy === "item"
                      || (item?.name || "").trim().length < 2}
                    onClick={() => act("item", () => api.post("/cafeteria/stock", item),
                      "Ingredient added").then((ok) => ok && setItem(null))}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              Add it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* movement */}
      <Dialog open={Boolean(move)} onOpenChange={(o) => !o && setMove(null)}>
        <DialogContent data-testid="movement-dialog">
          <DialogHeader><DialogTitle>{move?.item.name}</DialogTitle>
            <DialogDescription>
              {move?.item.level}{move?.item.unit} on hand.
            </DialogDescription></DialogHeader>
          {move && (
            <div className="grid grid-cols-2 gap-3">
              <F label="Movement">
                <Select value={move.kind} onValueChange={(v) => setMove({ ...move, kind: v })}>
                  <SelectTrigger data-testid="movement-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["received", "used", "wasted", "correction"].map((k) => (
                      <SelectItem key={k} value={k}>{k}</SelectItem>
                    ))}
                  </SelectContent>
                </Select></F>
              <F label="Quantity"><Input type="number" step="0.1" value={move.quantity}
                data-testid="movement-quantity"
                onChange={(e) => setMove({ ...move, quantity: Number(e.target.value) })} /></F>
              <div className="col-span-2">
                <F label="Note"><Input value={move.note} data-testid="movement-note"
                  onChange={(e) => setMove({ ...move, note: e.target.value })} /></F>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="movement-save" disabled={busy === "movement"}
                    onClick={() => act("movement", () => api.post(
                      `/cafeteria/stock/${move.item.item_ref}/movement`,
                      { quantity: move.quantity, kind: move.kind, note: move.note }),
                      "Stock updated").then((ok) => ok && setMove(null))}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              Record it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* takings */}
      <Dialog open={Boolean(money_)} onOpenChange={(o) => !o && setMoney(null)}>
        <DialogContent data-testid="takings-dialog">
          <DialogHeader><DialogTitle>Record takings</DialogTitle>
            <DialogDescription>
              {cfg.sales_mode === "fund"
                ? `Banked into ${cfg.takings_fund_id || "the fund set in Settings"}.`
                : "Charged onto the hirer's booking."}
            </DialogDescription></DialogHeader>
          {money_ && (
            <div className="grid grid-cols-2 gap-3">
              <F label="Day">
                <DateField value={money_.on_date} clearable={false} testId="takings-date"
                           onChange={(v) => setMoney({ ...money_, on_date: v })} /></F>
              <F label="Covers"><Input type="number" value={money_.covers}
                data-testid="takings-covers"
                onChange={(e) => setMoney({ ...money_, covers: Number(e.target.value) })} /></F>
              <F label="Amount"><Input type="number" value={money_.amount}
                data-testid="takings-amount"
                onChange={(e) => setMoney({ ...money_, amount: Number(e.target.value) })} /></F>
              <F label="Method"><Input value={money_.method} data-testid="takings-method"
                onChange={(e) => setMoney({ ...money_, method: e.target.value })} /></F>
              {cfg.sales_mode === "booking" && (
                <div className="col-span-2">
                  <F label="Booking reference">
                    <Input value={money_.booking_ref} data-testid="takings-booking"
                           onChange={(e) => setMoney({ ...money_,
                             booking_ref: e.target.value })} /></F>
                </div>
              )}
              <div className="col-span-2">
                <F label="Note"><Input value={money_.note} data-testid="takings-note"
                  onChange={(e) => setMoney({ ...money_, note: e.target.value })} /></F>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="takings-save"
                    disabled={busy === "takings" || !(money_?.amount > 0)}
                    onClick={() => act("takings", () => api.post("/cafeteria/takings", money_),
                      (res) => `${res.data.takings_ref} · ${res.data.destination}`)
                      .then((ok) => ok && setMoney(null))}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              Record it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
